import { useEffect, useState } from 'react';
import { localize, type Service, type StaffVisit } from '@360face/domain';
import { ApiError } from '../api/client';
import type { FreeMinutes, StaffApi } from './api';
import { contentLocale, tabletMoney, type TabletLocale, type TabletMessages } from './messages';
import { exceedsFreeTime } from './time';

/**
 * Owner 15.10.2026: after "Client arrived" the therapist changes the main treatment
 * (Express → Optimal) or adds extras; after "Finish" only extras (found during the massage).
 * Shows the price difference and warns if the next booking would be late (the therapist decides).
 */
export function ServicePicker({
  visit,
  catalog,
  api,
  mode,
  t,
  locale,
  onDone,
  onClose,
}: {
  visit: StaffVisit;
  catalog: Service[];
  api: StaffApi;
  mode: 'replace' | 'add';
  t: TabletMessages;
  locale: TabletLocale;
  onDone: (v: StaffVisit) => void;
  onClose: () => void;
}) {
  const lang = contentLocale(locale);
  const mains = visit.lines.filter((l) => l.kind === 'service' && l.ordered > 0);
  const [from, setFrom] = useState(mains[0]?.serviceId);
  const [to, setTo] = useState<string>();
  const [extras, setExtras] = useState<string[]>([]);
  const [free, setFree] = useState<FreeMinutes>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    api.freeMinutes(visit.id).then(setFree, () => undefined);
  }, [api, visit.id]);

  // only what this therapist does (when the catalog says so)
  const mine = (s: Service) => s.active && (s.staffIds.length === 0 || s.staffIds.includes(visit.staffId));
  const options =
    mode === 'replace'
      ? catalog.filter((s) => s.kind !== 'addon' && s.id !== from && mine(s))
      : catalog.filter((s) => s.kind === 'addon' && mine(s));
  const byId = (id?: string) => catalog.find((s) => s.id === id);
  const fromLine = visit.lines.find((l) => l.serviceId === from);
  const money = (m: number) => tabletMoney({ amountMinor: m, currency: 'EUR' }, locale);

  const addedMin =
    mode === 'replace'
      ? Math.max(0, (byId(to)?.durationMin ?? 0) - (byId(from)?.durationMin ?? 0))
      : extras.reduce((sum, id) => sum + (byId(id)?.durationMin ?? 0), 0);
  const ready = mode === 'replace' ? Boolean(from && to) : extras.length > 0;

  const apply = async () => {
    setBusy(true);
    setError(undefined);
    try {
      onDone(
        await api.changeServices(
          visit.id,
          mode === 'replace' ? { replace: { fromServiceId: from!, toServiceId: to! } } : { addIds: extras },
        ),
      );
    } catch (e) {
      setError(t.errors[e instanceof ApiError ? e.code : 'default'] ?? t.errors.default);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="t-card t-picker">
      <h3>{mode === 'replace' ? t.changeTreatment : visit.state === 'completed' ? t.addExtraAfter : t.addExtras}</h3>
      {mode === 'replace' && mains.length > 1 && (
        <div className="t-seg">
          {mains.map((l) => (
            <button key={l.serviceId} className={l.serviceId === from ? 'on' : ''} onClick={() => setFrom(l.serviceId)}>
              {l.title}
            </button>
          ))}
        </div>
      )}
      <p className="t-hint">{mode === 'replace' ? t.pickTreatment(fromLine?.title ?? '') : t.pickExtras}</p>
      <div className="t-picker-grid">
        {options.map((s) => {
          const on = mode === 'replace' ? to === s.id : extras.includes(s.id);
          const diff = mode === 'replace' && fromLine ? s.price.amountMinor - fromLine.unitPriceMinor : undefined;
          return (
            <button
              key={s.id}
              className={on ? 'on' : ''}
              aria-pressed={on}
              onClick={() =>
                mode === 'replace'
                  ? setTo(s.id)
                  : setExtras((x) => (x.includes(s.id) ? x.filter((i) => i !== s.id) : [...x, s.id]))
              }
            >
              <strong>{localize(s.title, lang)}</strong>
              <small>
                {s.durationMin} min · {money(s.price.amountMinor)}
                {diff !== undefined && diff !== 0 && ` (${diff > 0 ? '+' : '−'}${money(Math.abs(diff))})`}
              </small>
            </button>
          );
        })}
      </div>
      {free && exceedsFreeTime(addedMin, free.freeMinutes) && (
        <p className="t-warn" role="status">
          {t.noTime(addedMin, free.freeMinutes ?? 0, free.nextStart?.slice(11, 16) ?? '')}
        </p>
      )}
      {error && <p className="t-error">{error}</p>}
      <div className="t-actions">
        <button className="t-primary" disabled={busy || !ready} onClick={() => void apply()}>
          {t.applyChange}
          {addedMin > 0 && ` · ${t.extraTime(addedMin)}`}
        </button>
        <button disabled={busy} onClick={onClose}>
          {t.closePicker}
        </button>
      </div>
    </section>
  );
}
